/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Input Configuration Fields Component
 * 
 * A shared component for configuring input-type-specific fields.
 * Used by both PromptEditor (expert inputs) and SharedInputsPanel (shared inputs).
 * 
 * This ensures consistency between expert input configuration and shared input configuration.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Switch,
  Checkbox,
  Alert,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  InputAdornment,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { accountApi, indicatorApi, expertApi, modelLabDeploymentApi, type Account } from '../../services/api';
import { IndicatorSetPicker } from '../indicatorset/IndicatorSetPicker';
import { IndicatorConfigurator } from './IndicatorConfigurator';

// Input types matching backend AI/input_schema.py
export type InputType = 
  | 'expert'
  | 'indicator'
  | 'economic_calendar'
  | 'news_data'
  | 'cot_data'
  | 'options_data'
  | 'economic_indicators'
  | 'account_data'
  | 'open_positions'
  | 'pending_orders'
  | 'symbol_data'
  | 'trading_journal'
  | 'price_data'
  | 'previous_expert_results'
  | 'current_time'
  | 'static_text'
  | 'chart_image'
  | 'chart_image_file'
  // Storage Types
  | 'storage_text'
  | 'storage_file'
  // Global Storage Types
  | 'global_storage_text'
  | 'global_storage_file'
  // Knowledge Graph
  | 'knowledge_graph'
  // Model Lab
  | 'model_lab_prediction'
  // OpenBB Integration Types
  | 'stock_fundamentals'
  | 'openbb_news'
  | 'openbb_options'
  | 'openbb_forex'
  | 'openbb_crypto'
  | 'openbb_etf'
  | 'openbb_economic';

export interface ExpertOption {
  id: number;
  name: string;
}

/** Describes an already-defined expert-type input in the current expert. */
export interface ExpertInput {
  name: string;
  expert_id: number;
  expert_name?: string;
}

export interface InputConfigFieldsProps {
  inputType: InputType;
  config: Record<string, any>;
  onConfigChange: (config: Record<string, any>) => void;
  availableExperts?: ExpertOption[];
  disabled?: boolean;
  // For shared inputs, we don't need expert selection since they can't reference other experts
  hideExpertFields?: boolean;
  /** Already-defined expert-type inputs – used for the reference picker. */
  expertInputs?: ExpertInput[];
}

/**
 * ExpertRefButton – an inline button that opens a popover letting users
 * pick an output key from an upstream expert input to insert a
 * ``{{ expertInputName.key }}`` template reference into a config value.
 */
const ExpertRefButton: React.FC<{
  expertInputs: ExpertInput[];
  disabled?: boolean;
  onInsert: (ref: string) => void;
}> = ({ expertInputs, disabled, onInsert }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [outputKeysCache, setOutputKeysCache] = useState<Record<number, Array<{
    key: string; type: string; required: boolean; description: string;
  }>>>({});
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    // Pre-fetch output keys for all expert inputs
    expertInputs.forEach(async (ei) => {
      if (!outputKeysCache[ei.expert_id]) {
        setLoadingId(ei.expert_id);
        try {
          const data = await expertApi.getOutputKeys(ei.expert_id);
          setOutputKeysCache(prev => ({ ...prev, [ei.expert_id]: data.output_keys }));
        } catch {
          setOutputKeysCache(prev => ({ ...prev, [ei.expert_id]: [] }));
        } finally {
          setLoadingId(null);
        }
      }
    });
  };

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  if (expertInputs.length === 0) return null;

  return (
    <>
      <Tooltip title="Insert expert output reference">
        <IconButton
          size="small"
          onClick={handleOpen}
          disabled={disabled}
          sx={{ ml: 0.5 }}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ minWidth: 240, maxHeight: 320, overflow: 'auto' }}>
          <List dense>
            {expertInputs.map((ei) => {
              const keys = outputKeysCache[ei.expert_id];
              const isLoading = loadingId === ei.expert_id && !keys;
              return [
                <ListSubheader key={`header-${ei.expert_id}`} sx={{ lineHeight: '28px' }}>
                  {ei.expert_name || ei.name} (input: {ei.name})
                </ListSubheader>,
                isLoading ? (
                  <Box key={`loading-${ei.expert_id}`} sx={{ textAlign: 'center', py: 1 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : keys && keys.length > 0 ? (
                  keys.map((ok) => (
                    <ListItemButton
                      key={`${ei.expert_id}-${ok.key}`}
                      onClick={() => {
                        onInsert(`{{ ${ei.name}.${ok.key} }}`);
                        handleClose();
                      }}
                    >
                      <ListItemText
                        primary={ok.key}
                        secondary={ok.description || ok.type}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))
                ) : (
                  <ListItemButton key={`empty-${ei.expert_id}`} disabled>
                    <ListItemText
                      primary="No output keys defined"
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItemButton>
                ),
              ];
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
};

/**
 * Renders dynamic configuration fields based on input type.
 * This component is extracted from PromptEditor to ensure consistency
 * between expert input forms and shared input forms.
 */
export const InputConfigFields: React.FC<InputConfigFieldsProps> = ({
  inputType,
  config,
  onConfigChange,
  availableExperts = [],
  disabled = false,
  hideExpertFields = false,
  expertInputs = [],
}) => {
  // Account data
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  // Indicator data
  const [availableIndicators, setAvailableIndicators] = useState<Record<string, any>>({});
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [selectedIndicators, setSelectedIndicators] = useState<Array<{
    indicator_name: string;
    params?: Record<string, any>;
  }>>(config.indicators || []);

  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoadingAccounts(true);
      setAccountsError(null);
      try {
        const data = await accountApi.list();
        setAccounts(data);
      } catch (error: any) {
        console.error('Failed to fetch accounts:', error);
        setAccountsError(error?.response?.data?.error || 'Failed to load accounts');
      } finally {
        setLoadingAccounts(false);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch indicators on mount
  useEffect(() => {
    const fetchIndicators = async () => {
      setLoadingIndicators(true);
      try {
        const indicators = await indicatorApi.getAvailable();
        setAvailableIndicators(indicators);
      } catch (error) {
        console.error('Failed to fetch indicators:', error);
      } finally {
        setLoadingIndicators(false);
      }
    };
    fetchIndicators();
  }, []);

  // Sync selectedIndicators with config
  useEffect(() => {
    if (config.indicators) {
      setSelectedIndicators(config.indicators);
    }
  }, [config.indicators]);

  // Helper to update a single config field
  const updateField = (field: string, value: any) => {
    onConfigChange({ ...config, [field]: value });
  };

  // Helper to update multiple config fields
  const updateFields = (updates: Record<string, any>) => {
    onConfigChange({ ...config, ...updates });
  };

  // Handle indicator removal
  const handleRemoveIndicator = (index: number) => {
    const newIndicators = selectedIndicators.filter((_, i) => i !== index);
    setSelectedIndicators(newIndicators);
    updateField('indicators', newIndicators);
  };

  /**
   * Helper: render a text field with an optional expert reference button.
   * When upstream expert inputs exist, a link icon is shown that opens
   * a picker. Selecting a key inserts ``{{ inputName.key }}`` as the value.
   */
  const renderRefTextField = (
    field: string,
    label: string,
    opts: {
      required?: boolean;
      helperText?: string;
      placeholder?: string;
      type?: string;
      inputProps?: Record<string, any>;
    } = {},
  ) => {
    const hasExpertInputs = expertInputs.length > 0;
    const currentValue = config[field] || '';
    const isRef = typeof currentValue === 'string' && /\{\{.*?\}\}/.test(currentValue);

    return (
      <TextField
        fullWidth
        disabled={disabled}
        type={opts.type || 'text'}
        label={label}
        value={currentValue}
        onChange={(e) => updateField(field, e.target.value)}
        placeholder={opts.placeholder}
        required={opts.required}
        helperText={
          isRef
            ? `Dynamic value from expert output: ${currentValue}`
            : opts.helperText
        }
        InputProps={{
          ...opts.inputProps,
          endAdornment: hasExpertInputs ? (
            <InputAdornment position="end">
              <ExpertRefButton
                expertInputs={expertInputs}
                disabled={disabled}
                onInsert={(ref) => updateField(field, ref)}
              />
            </InputAdornment>
          ) : undefined,
        }}
        sx={isRef ? { '& .MuiOutlinedInput-root': { borderColor: 'info.main' } } : undefined}
      />
    );
  };

  // Render based on input type
  switch (inputType) {
    case 'expert':
      if (hideExpertFields) {
        return (
          <Alert severity="warning">
            Expert output type is not supported for shared inputs. 
            Shared inputs cannot reference other experts.
          </Alert>
        );
      }
      return (
        <FormControl fullWidth required disabled={disabled}>
          <InputLabel>Expert *</InputLabel>
          <Select
            value={config.expert_id || ''}
            onChange={(e) => {
              const expertId = e.target.value as number;
              const expert = availableExperts.find(exp => exp.id === expertId);
              updateFields({
                expert_id: expertId,
                expert_name: expert?.name || '',
              });
            }}
            label="Expert *"
          >
            {availableExperts.length === 0 ? (
              <MenuItem disabled value="">
                <em>No experts available in this system</em>
              </MenuItem>
            ) : (
              availableExperts.map((expert) => (
                <MenuItem key={expert.id} value={expert.id}>
                  {expert.name}
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>Select the expert whose output will be used as input</FormHelperText>
        </FormControl>
      );

    case 'indicator':
      const selectedAccount = accounts.find(acc => String(acc.id) === String(config.account_id));
      return (
        <>
          <Autocomplete
            fullWidth
            disabled={disabled}
            options={accounts}
            loading={loadingAccounts}
            getOptionLabel={(option) => `${option.name} (${option.platform}) - Balance: $${option.balance.toLocaleString()}`}
            value={selectedAccount || null}
            onChange={(_e, newValue) => {
              updateField('account_id', newValue?.id || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account *"
                placeholder="Select an account"
                required={!config.account_id}
                error={!!accountsError}
                helperText={accountsError || 'Select the account to fetch price data from'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" fontWeight="medium">
                    {option.name} ({option.platform})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {option.simple_id} | Balance: ${option.balance.toLocaleString()}
                  </Typography>
                </Box>
              </li>
            )}
          />
          {renderRefTextField('symbol', 'Symbol *', {
            required: true,
            placeholder: 'e.g., EURUSD, BTCUSD',
            helperText: 'Trading symbol for indicator calculation',
          })}
          <FormControl fullWidth required disabled={disabled}>
            <InputLabel>Timeframe *</InputLabel>
            <Select
              value={config.timeframe || 'H1'}
              onChange={(e) => updateField('timeframe', e.target.value)}
              label="Timeframe *"
            >
              <MenuItem value="M1">1 Minute (M1)</MenuItem>
              <MenuItem value="M5">5 Minutes (M5)</MenuItem>
              <MenuItem value="M15">15 Minutes (M15)</MenuItem>
              <MenuItem value="M30">30 Minutes (M30)</MenuItem>
              <MenuItem value="H1">1 Hour (H1)</MenuItem>
              <MenuItem value="H4">4 Hours (H4)</MenuItem>
              <MenuItem value="D1">1 Day (D1)</MenuItem>
              <MenuItem value="W1">1 Week (W1)</MenuItem>
              <MenuItem value="MN1">1 Month (MN1)</MenuItem>
            </Select>
            <FormHelperText>Select the timeframe for indicator calculation</FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            type="number"
            label="Candle Count (Optional)"
            value={config.count || ''}
            onChange={(e) => {
              const value = e.target.value;
              updateField('count', value ? parseInt(value) : undefined);
            }}
            placeholder="Leave empty for default"
            helperText="Number of candles to use for calculation"
            InputProps={{ inputProps: { min: 1, max: 10000 } }}
          />

          {/* Indicator Set vs Manual Selection */}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.use_indicator_set || false}
                  onChange={(e) => {
                    updateFields({
                      use_indicator_set: e.target.checked,
                      indicator_set_id: e.target.checked ? config.indicator_set_id : undefined,
                      indicators: e.target.checked ? undefined : config.indicators,
                    });
                  }}
                  disabled={disabled}
                />
              }
              label="Use Indicator Set"
            />
            <FormHelperText sx={{ mt: -0.5 }}>
              Use a predefined indicator set instead of selecting individual indicators
            </FormHelperText>
          </Box>

          {config.use_indicator_set ? (
            <Box sx={{ mt: 1 }}>
              <IndicatorSetPicker
                value={config.indicator_set_id || null}
                onChange={(indicatorSetId) => updateField('indicator_set_id', indicatorSetId)}
                label="Indicator Set"
                helperText="Select a predefined indicator set"
                required
              />
            </Box>
          ) : (
            <>
              {/* Manual Indicator Selection - simplified for shared inputs */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Indicators
                </Typography>
                <IndicatorConfigurator
                  availableIndicators={availableIndicators}
                  value={selectedIndicators[0] ? {
                    indicator_name: selectedIndicators[0].indicator_name,
                    params: selectedIndicators[0].params || {},
                  } : null}
                  onChange={(indicatorConfig) => {
                    const newIndicators = [{
                      indicator_name: indicatorConfig.indicator_name,
                      params: indicatorConfig.params,
                    }];
                    setSelectedIndicators(newIndicators);
                    updateField('indicators', newIndicators);
                  }}
                  showOutputInfo={true}
                />
                {selectedIndicators.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    {selectedIndicators.map((ind, index) => (
                      <Chip
                        key={index}
                        label={`${ind.indicator_name}${ind.params ? ` (${JSON.stringify(ind.params)})` : ''}`}
                        onDelete={() => handleRemoveIndicator(index)}
                        size="small"
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </>
          )}
        </>
      );

    case 'price_data':
      const priceAccount = accounts.find(acc => String(acc.id) === String(config.account_id));
      return (
        <>
          <Autocomplete
            fullWidth
            disabled={disabled}
            options={accounts}
            loading={loadingAccounts}
            getOptionLabel={(option) => `${option.name} (${option.platform}) - Balance: $${option.balance.toLocaleString()}`}
            value={priceAccount || null}
            onChange={(_e, newValue) => {
              updateField('account_id', newValue?.id || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account *"
                placeholder="Select an account"
                required={!config.account_id}
                error={!!accountsError}
                helperText={accountsError || 'Select the account to fetch price data from'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" fontWeight="medium">
                    {option.name} ({option.platform})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {option.simple_id} | Balance: ${option.balance.toLocaleString()}
                  </Typography>
                </Box>
              </li>
            )}
          />
          {renderRefTextField('symbol', 'Symbol *', {
            required: true,
            placeholder: 'e.g., EURUSD, BTCUSD',
            helperText: 'Trading symbol',
          })}
          <FormControl fullWidth required disabled={disabled}>
            <InputLabel>Timeframe *</InputLabel>
            <Select
              value={config.timeframe || 'H1'}
              onChange={(e) => updateField('timeframe', e.target.value)}
              label="Timeframe *"
            >
              <MenuItem value="M1">1 Minute (M1)</MenuItem>
              <MenuItem value="M5">5 Minutes (M5)</MenuItem>
              <MenuItem value="M15">15 Minutes (M15)</MenuItem>
              <MenuItem value="M30">30 Minutes (M30)</MenuItem>
              <MenuItem value="H1">1 Hour (H1)</MenuItem>
              <MenuItem value="H4">4 Hours (H4)</MenuItem>
              <MenuItem value="D1">1 Day (D1)</MenuItem>
              <MenuItem value="W1">1 Week (W1)</MenuItem>
              <MenuItem value="MN1">1 Month (MN1)</MenuItem>
            </Select>
            <FormHelperText>Select the timeframe for price candles</FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            type="number"
            label="Candle Count (Optional)"
            value={config.count || ''}
            onChange={(e) => {
              const value = e.target.value;
              updateField('count', value ? parseInt(value) : undefined);
            }}
            placeholder="Leave empty for default"
            helperText="Number of most recent candles to fetch"
            InputProps={{ inputProps: { min: 1, max: 10000 } }}
          />
        </>
      );

    case 'chart_image':
    case 'chart_image_file':
      const chartAccount = accounts.find(acc => String(acc.id) === String(config.account_id));
      return (
        <>
          <Autocomplete
            fullWidth
            disabled={disabled}
            options={accounts}
            loading={loadingAccounts}
            getOptionLabel={(option) => `${option.name} (${option.platform})`}
            value={chartAccount || null}
            onChange={(_e, newValue) => {
              updateField('account_id', newValue?.id || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account *"
                placeholder="Select an account"
                required={!config.account_id}
                error={!!accountsError}
                helperText={accountsError || 'Select the account for chart generation'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {renderRefTextField('symbol', 'Symbol *', {
            required: true,
            placeholder: 'e.g., EURUSD, BTCUSD',
          })}
          <FormControl fullWidth required disabled={disabled}>
            <InputLabel>Timeframe *</InputLabel>
            <Select
              value={config.timeframe || 'H1'}
              onChange={(e) => updateField('timeframe', e.target.value)}
              label="Timeframe *"
            >
              <MenuItem value="M1">1 Minute (M1)</MenuItem>
              <MenuItem value="M5">5 Minutes (M5)</MenuItem>
              <MenuItem value="M15">15 Minutes (M15)</MenuItem>
              <MenuItem value="M30">30 Minutes (M30)</MenuItem>
              <MenuItem value="H1">1 Hour (H1)</MenuItem>
              <MenuItem value="H4">4 Hours (H4)</MenuItem>
              <MenuItem value="D1">1 Day (D1)</MenuItem>
              <MenuItem value="W1">1 Week (W1)</MenuItem>
              <MenuItem value="MN1">1 Month (MN1)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            type="number"
            label="Candle Count"
            value={config.count || 100}
            onChange={(e) => updateField('count', parseInt(e.target.value) || 100)}
            InputProps={{ inputProps: { min: 10, max: 500 } }}
            helperText="Number of candles on chart (10-500)"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              disabled={disabled}
              type="number"
              label="Width"
              value={config.width || 1200}
              onChange={(e) => updateField('width', parseInt(e.target.value) || 1200)}
              InputProps={{ inputProps: { min: 400, max: 2000 } }}
              helperText="Chart width in pixels"
            />
            <TextField
              fullWidth
              disabled={disabled}
              type="number"
              label="Height"
              value={config.height || 800}
              onChange={(e) => updateField('height', parseInt(e.target.value) || 800)}
              InputProps={{ inputProps: { min: 300, max: 1500 } }}
              helperText="Chart height in pixels"
            />
          </Box>
          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Theme</InputLabel>
            <Select
              value={config.theme || 'dark'}
              onChange={(e) => updateField('theme', e.target.value)}
              label="Theme"
            >
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="light">Light</MenuItem>
            </Select>
          </FormControl>
          {inputType === 'chart_image_file' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Chart image will be sent as a file attachment to the LLM.
              Do NOT reference this input in the prompt text.
            </Alert>
          )}
        </>
      );

    case 'account_data': {
      // Support both account_ids (array) and account_id (single) for backward compatibility
      const accountDataIds = config.account_ids || (config.account_id ? [config.account_id] : []);
      return (
        <Autocomplete
          multiple
          fullWidth
          disabled={disabled}
          options={accounts}
          loading={loadingAccounts}
          getOptionLabel={(option) => `${option.name} (${option.platform}) - Balance: $${option.balance.toLocaleString()}`}
          value={accounts.filter(acc => accountDataIds.map(String).includes(String(acc.id)))}
          onChange={(_e, newValue) => {
            const accountIds = newValue.map(acc => acc.id);
            updateField('account_ids', accountIds);
            // Clear singular account_id if it was set
            if (config.account_id) {
              updateField('account_id', undefined);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Accounts *"
              placeholder="Select one or more accounts"
              required={!config.account_ids || config.account_ids.length === 0}
              error={!!accountsError}
              helperText={accountsError || 'Select the accounts to provide data for'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" fontWeight="medium">
                  {option.name} ({option.platform})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {option.simple_id} | Balance: ${option.balance.toLocaleString()}
                </Typography>
              </Box>
            </li>
          )}
        />
      );
    }

    case 'open_positions': {
      // Support both account_ids (array) and account_id (single) for backward compatibility
      const openPosIds = config.account_ids || (config.account_id ? [config.account_id] : []);
      return (
        <>
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={accounts}
            loading={loadingAccounts}
            getOptionLabel={(option) => `${option.name} (${option.platform})`}
            value={accounts.filter(acc => openPosIds.map(String).includes(String(acc.id)))}
            onChange={(_e, newValue) => {
              const accountIds = newValue.map(acc => acc.id);
              updateField('account_ids', accountIds);
              // Clear singular account_id if it was set
              if (config.account_id) {
                updateField('account_id', undefined);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Accounts *"
                placeholder="Select one or more accounts"
                required={!config.account_ids || config.account_ids.length === 0}
                error={!!accountsError}
                helperText={accountsError || 'Select accounts to fetch open positions from'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            fullWidth
            disabled={disabled}
            type="number"
            label="Record Limit (Optional)"
            value={config.count || ''}
            onChange={(e) => {
              const value = e.target.value;
              updateField('count', value ? parseInt(value) : undefined);
            }}
            placeholder="Leave empty for all positions"
            helperText="Limit to last N positions. Leave empty for all."
            InputProps={{ inputProps: { min: 1, max: 1000 } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.only_current_ai_system || false}
                onChange={(e) => updateField('only_current_ai_system', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Only Current AI System"
          />
          <FormHelperText sx={{ mt: -1, ml: 4 }}>
            Filter to only show positions created by this AI system
          </FormHelperText>
          <TextField
            fullWidth
            disabled={disabled}
            label="Label Filter (Optional)"
            value={config.label || ''}
            onChange={(e) => updateField('label', e.target.value || undefined)}
            placeholder="e.g., scalping, swing_trade"
            helperText="Filter to only show positions with this label"
          />
        </>
      );
    }

    case 'pending_orders': {
      // Support both account_ids (array) and account_id (single) for backward compatibility
      const pendingOrdersIds = config.account_ids || (config.account_id ? [config.account_id] : []);
      return (
        <>
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={accounts}
            loading={loadingAccounts}
            getOptionLabel={(option) => `${option.name} (${option.platform})`}
            value={accounts.filter(acc => pendingOrdersIds.map(String).includes(String(acc.id)))}
            onChange={(_e, newValue) => {
              const accountIds = newValue.map(acc => acc.id);
              updateField('account_ids', accountIds);
              // Clear singular account_id if it was set
              if (config.account_id) {
                updateField('account_id', undefined);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Accounts (Optional)"
                placeholder="Select accounts"
                helperText={accountsError || 'Leave empty for all accounts'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            Fetches all pending (limit/stop) orders. Leave accounts empty for all.
          </Alert>
        </>
      );
    }

    case 'symbol_data':
      return renderRefTextField('symbol', 'Symbol *', {
        required: true,
        placeholder: 'e.g., EURUSD, BTCUSD, XAUUSD',
        helperText: 'Trading symbol to get information for',
      });

    case 'economic_calendar':
      return (
        <>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            fullWidth
            disabled={disabled}
            options={[]}
            value={config.symbols || []}
            onChange={(_e, newValue) => {
              updateField('symbols', newValue as string[]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Symbols (Optional)"
                placeholder="e.g., EURUSD, GBPUSD"
                helperText="Filter by currency pairs (leave empty for all)"
              />
            )}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Count"
            type="number"
            value={config.count || 10}
            onChange={(e) => updateField('count', parseInt(e.target.value) || 10)}
            helperText="Maximum number of calendar events (default: 10)"
            InputProps={{ inputProps: { min: 1, max: 100 } }}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Duration (Days)"
            type="number"
            value={config.duration_days || 30}
            onChange={(e) => updateField('duration_days', parseInt(e.target.value) || 30)}
            helperText="Fetch events from the past N days (default: 30)"
            InputProps={{ inputProps: { min: 1, max: 365 } }}
          />
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={['High', 'Medium', 'Low']}
            value={config.impacts || []}
            onChange={(_e, newValue) => {
              updateField('impacts', newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Impact Levels (Optional)"
                placeholder="Select impact levels"
                helperText="Filter by impact level (leave empty for all)"
              />
            )}
          />
        </>
      );

    case 'news_data':
      return (
        <>
          <TextField
            fullWidth
            disabled={disabled}
            label="Count"
            type="number"
            value={config.count || 10}
            onChange={(e) => updateField('count', parseInt(e.target.value) || 10)}
            helperText="Maximum number of news articles (default: 10)"
            InputProps={{ inputProps: { min: 1, max: 100 } }}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Duration (Days) - Optional"
            type="number"
            value={config.duration_days || ''}
            onChange={(e) => updateField('duration_days', e.target.value ? parseInt(e.target.value) : undefined)}
            helperText="Fetch news from the past N days (leave empty for all)"
            InputProps={{ inputProps: { min: 1, max: 365 } }}
          />
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={[
              'inflation', 'employment', 'growth', 'housing', 'manufacturing',
              'consumer', 'trade', 'monetary_policy', 'fiscal_policy', 'central_bank',
              'geopolitics', 'risk_sentiment', 'commodities', 'equities', 'bonds',
              'earnings', 'crypto',
            ]}
            value={config.tags || []}
            onChange={(_e, newValue) => {
              updateField('tags', newValue as string[]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags (Optional)"
                placeholder="Select topic tags"
                helperText="Filter by topic tags (articles must match ALL selected tags)"
              />
            )}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Source (Optional)"
            value={config.source || ''}
            onChange={(e) => updateField('source', e.target.value || undefined)}
            placeholder="e.g. Forex Factory, Benzinga"
            helperText="Filter by news source (leave empty for all)"
          />
        </>
      );

    case 'cot_data':
      return (
        <>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            fullWidth
            disabled={disabled}
            options={[]}
            value={config.symbols || []}
            onChange={(_e, newValue) => {
              updateField('symbols', newValue as string[]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Symbols (Optional)"
                placeholder="e.g., EURO, GOLD"
                helperText="Filter by market/commodity name (leave empty for all)"
              />
            )}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Count"
            type="number"
            value={config.count || 10}
            onChange={(e) => updateField('count', parseInt(e.target.value) || 10)}
            helperText="Maximum number of COT reports (default: 10)"
            InputProps={{ inputProps: { min: 1, max: 100 } }}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Duration (Days) - Optional"
            type="number"
            value={config.duration_days || ''}
            onChange={(e) => updateField('duration_days', e.target.value ? parseInt(e.target.value) : undefined)}
            helperText="Fetch reports from the past N days (leave empty for all)"
            InputProps={{ inputProps: { min: 1, max: 365 } }}
          />
        </>
      );

    case 'trading_journal':
      return (
        <>
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={accounts}
            loading={loadingAccounts}
            getOptionLabel={(option) => `${option.name} (${option.platform})`}
            value={accounts.filter(acc => (config.account_ids || []).includes(acc.id))}
            onChange={(_e, newValue) => {
              const accountIds = newValue.map(acc => acc.id);
              updateField('account_ids', accountIds);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Accounts (Optional)"
                placeholder="Select accounts"
                helperText="Filter by accounts (leave empty for all)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Days"
            type="number"
            value={config.days || 30}
            onChange={(e) => updateField('days', parseInt(e.target.value) || 30)}
            helperText="Fetch entries from the past N days"
            InputProps={{ inputProps: { min: 1, max: 365 } }}
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Limit"
            type="number"
            value={config.limit || 20}
            onChange={(e) => updateField('limit', parseInt(e.target.value) || 20)}
            helperText="Maximum number of journal entries"
            InputProps={{ inputProps: { min: 1, max: 100 } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.only_current_ai_system || false}
                onChange={(e) => updateField('only_current_ai_system', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Only Current AI System"
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Label Filter (Optional)"
            value={config.label || ''}
            onChange={(e) => updateField('label', e.target.value || undefined)}
            placeholder="e.g., review, analysis"
            helperText="Filter by journal entry label"
          />
        </>
      );

    case 'options_data':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Options data from Yahoo Finance (free). For forex, use currency ETF symbols 
            (e.g., FXE for EUR/USD) or enter forex pair directly.
          </Alert>
          {renderRefTextField('symbol', 'Symbol *', {
            required: true,
            placeholder: 'e.g., AAPL, SPY, EURUSD, FXE',
            helperText: 'Stock/ETF ticker or forex pair',
          })}
          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Option Type</InputLabel>
            <Select
              value={config.option_type || 'both'}
              onChange={(e) => updateField('option_type', e.target.value)}
              label="Option Type"
            >
              <MenuItem value="both">Both (Calls & Puts)</MenuItem>
              <MenuItem value="calls">Calls Only</MenuItem>
              <MenuItem value="puts">Puts Only</MenuItem>
            </Select>
            <FormHelperText>Type of options to fetch</FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            label="Near The Money"
            type="number"
            value={config.near_the_money ?? 10}
            onChange={(e) => updateField('near_the_money', parseInt(e.target.value) || 10)}
            InputProps={{ inputProps: { min: 1, max: 50 } }}
            helperText="Number of strikes around current price (default: 10)"
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Max Expirations"
            type="number"
            value={config.max_expirations ?? 3}
            onChange={(e) => updateField('max_expirations', parseInt(e.target.value) || 3)}
            InputProps={{ inputProps: { min: 1, max: 10 } }}
            helperText="Maximum expiration dates to fetch (default: 3)"
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Specific Expiration Date (Optional)"
            type="date"
            value={config.expiration_date || ''}
            onChange={(e) => updateField('expiration_date', e.target.value || undefined)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to auto-select nearest expirations"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_greeks ?? true}
                onChange={(e) => updateField('include_greeks', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include Greeks (Delta, Gamma, Theta, Vega)"
          />
        </>
      );

    case 'economic_indicators':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Economic indicators from FRED (Federal Reserve Economic Data).
            Compare GDP, CPI, unemployment, and more across currencies.
          </Alert>
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD']}
            value={config.currencies || ['USD']}
            onChange={(_e, newValue) => {
              updateField('currencies', newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Currencies *"
                placeholder="Select currencies"
                required
                helperText="Select currencies to compare economic indicators"
              />
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_comparison ?? true}
                onChange={(e) => updateField('include_comparison', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include change vs previous period"
          />
        </>
      );

    case 'previous_expert_results':
      if (hideExpertFields) {
        return (
          <Alert severity="warning">
            Previous expert results type is not supported for shared inputs.
            Shared inputs cannot reference expert execution history.
          </Alert>
        );
      }
      return (
        <>
          <FormControl fullWidth required disabled={disabled}>
            <InputLabel>Expert *</InputLabel>
            <Select
              value={config.expert_id || ''}
              onChange={(e) => {
                const expertId = e.target.value as number;
                const expert = availableExperts.find(exp => exp.id === expertId);
                updateFields({
                  expert_id: expertId,
                  expert_name: expert?.name || '',
                });
              }}
              label="Expert *"
            >
              {availableExperts.length === 0 ? (
                <MenuItem disabled value="">
                  <em>No experts available</em>
                </MenuItem>
              ) : (
                availableExperts.map((expert) => (
                  <MenuItem key={expert.id} value={expert.id}>
                    {expert.name}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>Select expert whose previous results to fetch</FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            label="Count"
            type="number"
            value={config.count || 10}
            onChange={(e) => updateField('count', parseInt(e.target.value) || 10)}
            InputProps={{ inputProps: { min: 1, max: 100 } }}
            helperText="Number of previous results to fetch (default: 10)"
          />
        </>
      );

    case 'current_time':
      return (
        <Alert severity="info">
          This input automatically provides the current UTC time in multiple formats:
          ISO datetime, date only, time only, timestamp, and formatted string.
          No configuration needed.
        </Alert>
      );

    case 'static_text':
      return (
        <>
          <TextField
            fullWidth
            disabled={disabled}
            label="Label (Optional)"
            value={config.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., User Instructions"
            helperText="A display label for this input"
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Default Text"
            value={config.text || ''}
            onChange={(e) => updateField('text', e.target.value)}
            placeholder="Enter default text value"
            multiline
            rows={3}
            helperText="Pre-fill text (can be overridden at runtime)"
          />
        </>
      );

    // OpenBB Platform Types
    case 'stock_fundamentals':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB Stock Fundamentals</strong> - Company profiles, financials, ratios from 60+ providers.
          </Alert>
          {renderRefTextField('symbol', 'Symbol *', {
            required: true,
            placeholder: 'e.g., AAPL, MSFT, GOOGL',
            helperText: 'Stock ticker symbol',
          })}
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={['profile', 'income', 'balance', 'ratios', 'earnings', 'cash_flow', 'dividends', 'splits']}
            value={config.data_types || ['profile', 'ratios']}
            onChange={(_e, newValue) => {
              updateField('data_types', newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Data Types"
                placeholder="Select data to fetch"
                helperText="Choose which fundamental data to include"
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Period</InputLabel>
              <Select
                value={config.period || 'annual'}
                onChange={(e) => updateField('period', e.target.value)}
                label="Period"
              >
                <MenuItem value="annual">Annual</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="ttm">TTM</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              disabled={disabled}
              label="Historical Periods"
              type="number"
              value={config.limit || 4}
              onChange={(e) => updateField('limit', parseInt(e.target.value) || 4)}
              InputProps={{ inputProps: { min: 1, max: 20 } }}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={config.include_estimates ?? false}
                onChange={(e) => updateField('include_estimates', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include analyst estimates"
          />
        </>
      );

    case 'openbb_news':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB Financial News</strong> - Multi-source news from Benzinga, FMP, Intrinio.
          </Alert>
          <FormControl fullWidth disabled={disabled}>
            <InputLabel>News Type</InputLabel>
            <Select
              value={config.source_type || 'world'}
              onChange={(e) => updateField('source_type', e.target.value)}
              label="News Type"
            >
              <MenuItem value="world">World/Market News</MenuItem>
              <MenuItem value="company">Company-Specific News</MenuItem>
            </Select>
          </FormControl>
          {config.source_type === 'company' && (
            <TextField
              fullWidth
              disabled={disabled}
              required
              label="Symbols *"
              value={(config.symbols || []).join(', ')}
              onChange={(e) => {
                const symbols = e.target.value
                  .split(/[,\s]+/)
                  .map((s: string) => s.trim())
                  .filter((s: string) => s.length > 0);
                updateField('symbols', symbols);
              }}
              placeholder="e.g., AAPL, MSFT"
              helperText="Enter stock symbols (required for company news)"
            />
          )}
          <TextField
            fullWidth
            disabled={disabled}
            label="Max Articles"
            type="number"
            value={config.limit || 20}
            onChange={(e) => updateField('limit', parseInt(e.target.value) || 20)}
            InputProps={{ inputProps: { min: 1, max: 100 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_content ?? false}
                onChange={(e) => updateField('include_content', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include full article content"
          />
        </>
      );

    case 'openbb_options':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB Options Chain</strong> - Options with Greeks from CBOE, Intrinio, Tradier.
          </Alert>
          {renderRefTextField('symbol', 'Symbol *', {
            required: true,
            placeholder: 'e.g., AAPL, SPY',
          })}
          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Option Type</InputLabel>
            <Select
              value={config.option_type || 'both'}
              onChange={(e) => updateField('option_type', e.target.value)}
              label="Option Type"
            >
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="calls">Calls Only</MenuItem>
              <MenuItem value="puts">Puts Only</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            label="Expiration Date (Optional)"
            type="date"
            value={config.expiration || ''}
            onChange={(e) => updateField('expiration', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_unusual ?? false}
                onChange={(e) => updateField('include_unusual', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include unusual options activity"
          />
        </>
      );

    case 'openbb_forex':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB Forex Data</strong> - Currency quotes from Polygon, FMP, Yahoo.
          </Alert>
          <TextField
            fullWidth
            disabled={disabled}
            required
            label="Currency Pairs *"
            value={(config.pairs || []).join(', ')}
            onChange={(e) => {
              const pairs = e.target.value
                .split(/[,\s]+/)
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0);
              updateField('pairs', pairs);
            }}
            placeholder="e.g., EURUSD, GBPUSD"
            helperText="Enter currency pairs separated by commas"
          />
          <FormControl fullWidth disabled={disabled}>
            <InputLabel>Data Type</InputLabel>
            <Select
              value={(config.data_types || ['historical'])[0] || 'historical'}
              onChange={(e) => updateField('data_types', [e.target.value])}
              label="Data Type"
            >
              <MenuItem value="quote">Current Quote</MenuItem>
              <MenuItem value="historical">Historical OHLCV</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            disabled={disabled}
            label="Bars Count"
            type="number"
            value={config.bars_count || 100}
            onChange={(e) => updateField('bars_count', parseInt(e.target.value) || 100)}
            InputProps={{ inputProps: { min: 1, max: 1000 } }}
            helperText="Number of price bars for historical data"
          />
        </>
      );

    case 'openbb_crypto':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB Crypto Data</strong> - Cryptocurrency prices and data.
          </Alert>
          <TextField
            fullWidth
            disabled={disabled}
            required
            label="Crypto Symbols *"
            value={(config.crypto_symbols || []).join(', ')}
            onChange={(e) => {
              const symbols = e.target.value
                .split(/[,\s]+/)
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0);
              updateField('crypto_symbols', symbols);
            }}
            placeholder="e.g., BTCUSD, ETHUSD"
            helperText="Enter crypto symbols separated by commas"
          />
          <TextField
            fullWidth
            disabled={disabled}
            label="Bars Count"
            type="number"
            value={config.bars_count || 100}
            onChange={(e) => updateField('bars_count', parseInt(e.target.value) || 100)}
            InputProps={{ inputProps: { min: 1, max: 1000 } }}
          />
        </>
      );

    case 'openbb_etf':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB ETF Data</strong> - ETF holdings and information.
          </Alert>
          {renderRefTextField('symbol', 'ETF Symbol *', {
            required: true,
            placeholder: 'e.g., SPY, QQQ, IWM',
          })}
          <TextField
            fullWidth
            disabled={disabled}
            label="Holdings Limit"
            type="number"
            value={config.holdings_limit || 20}
            onChange={(e) => updateField('holdings_limit', parseInt(e.target.value) || 20)}
            InputProps={{ inputProps: { min: 1, max: 100 } }}
            helperText="Number of top holdings to include"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_sectors ?? true}
                onChange={(e) => updateField('include_sectors', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include sector breakdown"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_countries ?? false}
                onChange={(e) => updateField('include_countries', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include country breakdown"
          />
        </>
      );

    case 'openbb_economic':
      return (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>OpenBB Economic Data</strong> - GDP, CPI, interest rates from 60+ sources.
          </Alert>
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={['United States', 'Eurozone', 'United Kingdom', 'Japan', 'China', 'Canada', 'Australia']}
            value={config.countries || ['United States']}
            onChange={(_e, newValue) => {
              updateField('countries', newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Countries"
                placeholder="Select countries"
              />
            )}
          />
          <Autocomplete
            multiple
            fullWidth
            disabled={disabled}
            options={['gdp', 'cpi', 'unemployment', 'interest_rates', 'pmi', 'retail_sales', 'industrial_production']}
            value={config.economic_indicators || ['gdp', 'cpi']}
            onChange={(_e, newValue) => {
              updateField('economic_indicators', newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Economic Indicators"
                placeholder="Select indicators"
              />
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_calendar ?? false}
                onChange={(e) => updateField('include_calendar', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include economic calendar"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_forecast ?? false}
                onChange={(e) => updateField('include_forecast', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include forecasts"
          />
        </>
      );

    case 'knowledge_graph':
      return (
        <>
          <TextField
            select
            label="Query Type"
            value={config.query_type ?? 'currency_context'}
            onChange={(e) => updateField('query_type', e.target.value)}
            fullWidth
            disabled={disabled}
            helperText="Type of knowledge graph query to run"
          >
            <MenuItem value="currency_context">Currency Context</MenuItem>
            <MenuItem value="entity_context">Entity Context</MenuItem>
            <MenuItem value="causal_chain">Causal Chain</MenuItem>
            <MenuItem value="historical_effects">Historical Effects</MenuItem>
          </TextField>

          {(config.query_type === 'currency_context' || config.query_type === 'historical_effects' || !config.query_type) && (
            <TextField
              label="Currency"
              value={config.currency ?? 'USD'}
              onChange={(e) => updateField('currency', e.target.value)}
              fullWidth
              disabled={disabled}
              helperText="Currency code (e.g. USD, EUR, GBP)"
            />
          )}

          {config.query_type === 'entity_context' && (
            <TextField
              label="Entity Name"
              value={config.entity ?? ''}
              onChange={(e) => updateField('entity', e.target.value)}
              fullWidth
              disabled={disabled}
              helperText="Entity name (e.g. Federal Reserve, ECB)"
            />
          )}

          {config.query_type === 'causal_chain' && (
            <>
              <TextField
                label="Event ID"
                type="number"
                value={config.event_id ?? ''}
                onChange={(e) => updateField('event_id', parseInt(e.target.value) || 0)}
                fullWidth
                disabled={disabled}
                helperText="MarketEvent ID to trace causal chain from"
              />
              <TextField
                select
                label="Direction"
                value={config.direction ?? 'backward'}
                onChange={(e) => updateField('direction', e.target.value)}
                fullWidth
                disabled={disabled}
                helperText="backward = why did this happen, forward = what did this cause"
              >
                <MenuItem value="backward">Backward (causes)</MenuItem>
                <MenuItem value="forward">Forward (effects)</MenuItem>
              </TextField>
              <TextField
                label="Max Depth"
                type="number"
                value={config.max_depth ?? 4}
                onChange={(e) => updateField('max_depth', parseInt(e.target.value) || 4)}
                fullWidth
                disabled={disabled}
                helperText="Maximum chain hops (default: 4)"
              />
            </>
          )}

          {config.query_type === 'historical_effects' && (
            <TextField
              label="Event Type"
              value={config.event_type ?? 'MACRO_RELEASE'}
              onChange={(e) => updateField('event_type', e.target.value)}
              fullWidth
              disabled={disabled}
              helperText="Filter by event type (e.g. MACRO_RELEASE, CENTRAL_BANK)"
            />
          )}

          <TextField
            label="Days Back"
            type="number"
            value={config.days_back ?? 7}
            onChange={(e) => updateField('days_back', parseInt(e.target.value) || 7)}
            fullWidth
            disabled={disabled}
            helperText="How many days of history to query"
          />

          <TextField
            label="Limit"
            type="number"
            value={config.limit ?? 30}
            onChange={(e) => updateField('limit', parseInt(e.target.value) || 30)}
            fullWidth
            disabled={disabled}
            helperText="Maximum events to return"
          />
        </>
      );

    case 'storage_text':
      return (
        <>
          <TextField
            label="Storage Name"
            value={config.storage_name ?? ''}
            onChange={(e) => updateField('storage_name', e.target.value)}
            fullWidth
            disabled={disabled}
            helperText="Name of a specific storage item (leave blank to use tag filter)"
          />
          <TextField
            label="Storage Tags (comma-separated)"
            value={(config.storage_tags ?? []).join(', ')}
            onChange={(e) => updateField('storage_tags', e.target.value.split(',').map((t: string) => t.trim()).filter(Boolean))}
            fullWidth
            disabled={disabled}
            helperText="Filter storage items by tags"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.match_all_tags ?? false}
                onChange={(e) => updateField('match_all_tags', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Match all tags (AND logic)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_metadata ?? false}
                onChange={(e) => updateField('include_metadata', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include metadata"
          />
        </>
      );

    case 'storage_file':
      return (
        <>
          <TextField
            label="Storage Name"
            value={config.storage_name ?? ''}
            onChange={(e) => updateField('storage_name', e.target.value)}
            fullWidth
            disabled={disabled}
            helperText="Name of a specific storage file (leave blank to use tag filter)"
          />
          <TextField
            label="Storage Tags (comma-separated)"
            value={(config.storage_tags ?? []).join(', ')}
            onChange={(e) => updateField('storage_tags', e.target.value.split(',').map((t: string) => t.trim()).filter(Boolean))}
            fullWidth
            disabled={disabled}
            helperText="Filter storage files by tags"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.extract_text ?? true}
                onChange={(e) => updateField('extract_text', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Extract text content"
          />
        </>
      );

    case 'global_storage_text':
      return (
        <>
          <TextField
            label="Storage Name"
            value={config.storage_name ?? ''}
            onChange={(e) => updateField('storage_name', e.target.value)}
            fullWidth
            disabled={disabled}
            helperText="Name of a specific global storage item (leave blank to use tag filter)"
          />
          <TextField
            label="Storage Tags (comma-separated)"
            value={(config.storage_tags ?? []).join(', ')}
            onChange={(e) => updateField('storage_tags', e.target.value.split(',').map((t: string) => t.trim()).filter(Boolean))}
            fullWidth
            disabled={disabled}
            helperText="Filter global storage items by tags"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.match_all_tags ?? false}
                onChange={(e) => updateField('match_all_tags', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Match all tags (AND logic)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.include_metadata ?? false}
                onChange={(e) => updateField('include_metadata', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Include metadata"
          />
        </>
      );

    case 'global_storage_file':
      return (
        <>
          <TextField
            label="Storage Name"
            value={config.storage_name ?? ''}
            onChange={(e) => updateField('storage_name', e.target.value)}
            fullWidth
            disabled={disabled}
            helperText="Name of a specific global storage file (leave blank to use tag filter)"
          />
          <TextField
            label="Storage Tags (comma-separated)"
            value={(config.storage_tags ?? []).join(', ')}
            onChange={(e) => updateField('storage_tags', e.target.value.split(',').map((t: string) => t.trim()).filter(Boolean))}
            fullWidth
            disabled={disabled}
            helperText="Filter global storage files by tags"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.extract_text ?? true}
                onChange={(e) => updateField('extract_text', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Extract text content"
          />
        </>
      );

    case 'model_lab_prediction':
      return <ModelLabPredictionConfig config={config} updateField={updateField} disabled={disabled} />;

    default:
      return (
        <Alert severity="warning">
          Configuration not available for input type: {inputType}
        </Alert>
      );
  }
};

// ---------------------------------------------------------------------------
// Model Lab Prediction sub-form
// ---------------------------------------------------------------------------

interface ModelLabPredictionConfigProps {
  config: Record<string, any>;
  updateField: (key: string, value: any) => void;
  disabled?: boolean;
}

const ModelLabPredictionConfig: React.FC<ModelLabPredictionConfigProps> = ({
  config,
  updateField,
  disabled,
}) => {
  const [deployments, setDeployments] = useState<Array<{ id: number; name: string; status: string; endpoint_slug?: string }>>([]);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [depsError, setDepsError] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<Array<{ name: string; description?: string }>>([]);
  const [testResult, setTestResult] = useState<any | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDeps(true);
      setDepsError(null);
      try {
        const res = await modelLabDeploymentApi.list({ status: 'ACTIVE' } as any);
        const items = (res as any)?.results ?? res ?? [];
        if (!cancelled) setDeployments(items);
      } catch (e: any) {
        if (!cancelled) setDepsError(e?.response?.data?.error || e?.message || 'Failed to load deployments');
      } finally {
        if (!cancelled) setLoadingDeps(false);
      }
      try {
        const pl = await modelLabDeploymentApi.pipelines();
        if (!cancelled) setPipelines(pl || []);
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedDep = deployments.find((d) => d.id === config.deployment_id);

  const runTest = async () => {
    if (!config.deployment_id) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const res = await modelLabDeploymentApi.predictLive(config.deployment_id, {
        rows: config.rows || 1,
        symbol: config.symbol,
        timeframe: config.timeframe,
        lookback_bars: config.lookback_bars,
      });
      setTestResult(res);
    } catch (e: any) {
      setTestError(e?.response?.data?.error || e?.message || 'Prediction failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 1 }}>
        Calls the deployment's <b>live predict</b> endpoint. The deployment's feature_recipe
        auto-fetches market data and builds features — you do not pass features here.
      </Alert>

      <FormControl fullWidth disabled={disabled || loadingDeps}>
        <InputLabel>Deployment</InputLabel>
        <Select
          label="Deployment"
          value={config.deployment_id ?? ''}
          onChange={(e) => updateField('deployment_id', Number(e.target.value) || undefined)}
        >
          {deployments.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              #{d.id} — {d.name} <Chip size="small" label={d.status} sx={{ ml: 1 }} />
            </MenuItem>
          ))}
          {deployments.length === 0 && !loadingDeps && (
            <MenuItem disabled value="">
              No ACTIVE deployments found
            </MenuItem>
          )}
        </Select>
        <FormHelperText>
          {depsError ? depsError : 'Only ACTIVE deployments with a feature_recipe will work'}
        </FormHelperText>
      </FormControl>

      <TextField
        label="Rows"
        type="number"
        value={config.rows ?? 1}
        onChange={(e) => updateField('rows', Math.max(1, parseInt(e.target.value) || 1))}
        fullWidth
        disabled={disabled}
        helperText="Number of most-recent predictions to return (default 1)"
      />

      <Stack direction="row" spacing={1}>
        <TextField
          label="Symbol (override)"
          value={config.symbol ?? ''}
          onChange={(e) => updateField('symbol', e.target.value || undefined)}
          fullWidth
          disabled={disabled}
          placeholder="from recipe"
          helperText="Leave empty to use the deployment's recipe"
        />
        <TextField
          label="Timeframe (override)"
          value={config.timeframe ?? ''}
          onChange={(e) => updateField('timeframe', e.target.value || undefined)}
          fullWidth
          disabled={disabled}
          placeholder="from recipe"
          helperText="e.g. D1, H1, H4"
        />
      </Stack>

      <Stack direction="row" spacing={1}>
        <TextField
          label="Lookback Bars (override)"
          type="number"
          value={config.lookback_bars ?? ''}
          onChange={(e) => {
            const v = parseInt(e.target.value);
            updateField('lookback_bars', isNaN(v) ? undefined : v);
          }}
          fullWidth
          disabled={disabled}
          placeholder="from recipe"
          helperText="Bars to fetch for feature window"
        />
        <FormControl fullWidth disabled={disabled || pipelines.length === 0}>
          <InputLabel>Pipeline (override)</InputLabel>
          <Select
            label="Pipeline (override)"
            value={config.pipeline ?? ''}
            onChange={(e) => updateField('pipeline', e.target.value || undefined)}
          >
            <MenuItem value="">(use recipe's pipeline)</MenuItem>
            {pipelines.map((p) => (
              <MenuItem key={p.name} value={p.name}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Available feature pipelines</FormHelperText>
        </FormControl>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.include_probabilities ?? true}
              onChange={(e) => updateField('include_probabilities', e.target.checked)}
              disabled={disabled}
            />
          }
          label="Include probabilities"
        />
        <FormControlLabel
          control={
            <Switch
              checked={config.include_features ?? false}
              onChange={(e) => updateField('include_features', e.target.checked)}
              disabled={disabled}
            />
          }
          label="Include computed features"
        />
      </Stack>

      {selectedDep && (
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Template variables exposed (replace <code>name</code> with the input's name):
          </Typography>
          <Box component="pre" sx={{ m: 0, mt: 0.5, fontSize: 11, whiteSpace: 'pre-wrap' }}>
{`{{ name }}                    # primary prediction (scalar if rows=1)
{{ name_predictions }}        # full list
{{ name_probabilities }}      # list of class-proba lists
{{ name_confidence }}         # max proba for first row
{{ name_last_bar_time }}      # ISO timestamp
{{ name_last_close }}         # close price
{{ name_symbol }}             # symbol fetched
{{ name_latency_ms }}         # inference latency
{{ name_deployment }}         # { id, name, endpoint_slug }`}
          </Box>
        </Box>
      )}

      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Chip
            label={testing ? 'Testing…' : 'Test live prediction'}
            color="primary"
            variant="outlined"
            clickable={!disabled && !!config.deployment_id && !testing}
            onClick={!disabled && !!config.deployment_id && !testing ? runTest : undefined}
          />
          {testResult && (
            <Chip size="small" color="success" label={`latency ${testResult.latency_ms}ms`} />
          )}
        </Stack>
        {testError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {testError}
          </Alert>
        )}
        {testResult && (
          <Box
            component="pre"
            sx={{
              mt: 1,
              p: 1,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              fontSize: 11,
              maxHeight: 220,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(testResult, null, 2)}
          </Box>
        )}
      </Box>
    </>
  );
};

export default InputConfigFields;